import React, { useState, useEffect } from 'react';
import { Box, Typography, List, ListItem, ListItemText } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import * as turf from '@turf/turf';
import DashboardMetricCard from './DashboardMetricCard';

const METERS_PER_NM = 1852;
const NM_PER_STATUTE_MILE = 0.868976;
const NEAR_NO_GO_CAUTION_NM = 0.25 * NM_PER_STATUTE_MILE;
const NEAR_NO_GO_ADVISORY_NM = 2 * NM_PER_STATUTE_MILE;
const NEAR_OBSTACLE_ADVISORY_NM = 2 * NM_PER_STATUTE_MILE;

function distanceFromProtectedAreaNm(area, lat, lon) {
  const point = turf.point([Number(lon), Number(lat)]);
  if (area.geojson) {
    try {
      const polygon = area.geojson.type === 'Feature'
        ? area.geojson
        : { type: 'Feature', properties: {}, geometry: area.geojson };
      if (turf.booleanPointInPolygon(point, polygon)) {
        return 0;
      }
      return Math.max(0, turf.pointToPolygonDistance(point, polygon, { units: 'nauticalmiles' }));
    } catch (err) {
      console.warn('Protected area geometry distance failed:', err);
    }
  }

  if (area.lat && area.lon) {
    return turf.distance(point, turf.point([Number(area.lon), Number(area.lat)]), { units: 'nauticalmiles' });
  }

  return Infinity;
}

function protectedAreaImpact(area, distanceNm) {
  const baseSeverity = area.severity || 'advisory';
  const isNoGoArea = baseSeverity === 'no-go';
  const inside = distanceNm === 0;

  if (isNoGoArea && inside) return 'no-go';
  if (isNoGoArea && distanceNm <= NEAR_NO_GO_CAUTION_NM) return 'caution';
  if (isNoGoArea && distanceNm <= NEAR_NO_GO_ADVISORY_NM) return 'advisory';
  if (baseSeverity === 'caution' && inside) return 'advisory';
  return 'info';
}

function impactRank(severity) {
  return { 'no-go': 4, caution: 3, advisory: 2, info: 1, go: 0 }[severity] || 0;
}

const NearbyHazardsCardDashboard = ({ lat, lon, refreshTrigger, onUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [severity, setSeverity] = useState('unknown');
  const [hazardsData, setHazardsData] = useState([]);

  useEffect(() => {
    const fetchHazards = async () => {
      if (!lat || !lon) {
        setSeverity('advisory');
        onUpdate && onUpdate('advisory', ['No location set']);
        setLoading(false);
        return;
      }

      // Only show loading on first load
      if (severity === 'unknown') {
        setLoading(true);
      }
      try {
        const protectedAreasPromise = fetch(
          `/API/protected-areas.php?lat=${lat}&lon=${lon}&radius=1000`
        )
          .then((protectedRes) => {
            if (!protectedRes.ok) throw new Error(`Protected areas API returned ${protectedRes.status}`);
            return protectedRes.json();
          });
        const url = `/API/check-airspacev2.php?lat=${lat}&lon=${lon}&radius=5000&type=all`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Hazard API returned ${res.status}`);
        const data = await res.json();

        let sev = 'go';
        let partialDataError = false;

        // Check obstacles
        const obstacles = data.obs?.features || [];
        const obstacleList = obstacles.map(feat => {
          const elev = parseInt(feat.properties?.elevation_agl ?? feat.properties?.elevation ?? 0);
          const distM = feat.properties?.distance_m ?? 0;
          return {
            type: 'Obstacle',
            description: `${elev} ft AGL`,
            distance: distM / METERS_PER_NM, // Convert meters to NM
            severity: elev <= 400 && (distM / METERS_PER_NM) <= NEAR_OBSTACLE_ADVISORY_NM ? 'advisory' : 'info',
            reason: `${elev} ft AGL obstacle is ${((distM / METERS_PER_NM) * 1.15078).toFixed(1)} miles away`
          };
        });

        // Check restricted areas
        const raa = data.raa?.features || [];
        const restrictedList = raa.map(feat => ({
          type: 'Restricted Area',
          description: feat.properties?.name || 'Unknown',
          distance: (feat.properties?.distance_m ?? 0) / METERS_PER_NM,
          severity: ((feat.properties?.distance_m ?? 0) / METERS_PER_NM) <= NEAR_NO_GO_CAUTION_NM ? 'caution' : 'advisory',
          reason: `${feat.properties?.name || 'Restricted area'} is ${((((feat.properties?.distance_m ?? 0) / METERS_PER_NM) * 1.15078)).toFixed(1)} miles away and may limit drone operations`
        }));

        // Check airports
        const airports = data.apt?.features || [];
        const airportList = airports.filter(apt => {
          const distM = apt.properties?.distance_m ?? Infinity;
          return distM / 1609.34 < 3; // Within 3 miles
        }).map(apt => ({
          type: 'Airport',
          description: apt.properties?.name || 'Unknown',
          distance: (apt.properties?.distance_m ?? 0) / METERS_PER_NM,
          severity: 'advisory',
          reason: `${apt.properties?.name || 'Airport/heliport'} is ${((((apt.properties?.distance_m ?? 0) / METERS_PER_NM) * 1.15078)).toFixed(1)} miles away`
        }));

        let protectedList = [];
        try {
          const protectedData = await protectedAreasPromise;
          protectedList = (protectedData.protectedAreas || [])
            .map(area => {
              const distance = distanceFromProtectedAreaNm(area, lat, lon);
              const impact = protectedAreaImpact(area, distance);
              return {
                type: area.category || 'Protected Area',
                description: area.name || 'Park or protected area',
                distance,
                source: 'protected-area',
                severity: impact,
                rule: area.rule,
                geometryType: area.geometry_type || (area.geojson ? area.geojson.type : 'point-radius'),
                reason: `${area.name || 'Protected area'} ${distance === 0 ? 'contains this location' : `is ${(distance * 1.15078).toFixed(1)} miles away`} - ${area.rule || 'verify local park rules'}`
              };
            })
            .filter(area => area.severity !== 'info' || area.distance <= NEAR_NO_GO_ADVISORY_NM);
        } catch (protectedErr) {
          console.warn('Protected area lookup failed:', protectedErr);
          partialDataError = true;
        }

        const allHazards = [...protectedList, ...obstacleList, ...restrictedList, ...airportList]
          .sort((a, b) => a.distance - b.distance);

        setHazardsData(allHazards);

        // Determine severity
        const impactHazards = allHazards.filter(hazard => ['no-go', 'caution', 'advisory'].includes(hazard.severity));
        if (protectedList.some(area => area.severity === 'no-go')) {
          sev = 'no-go';
        } else if (allHazards.some(hazard => hazard.severity === 'caution')) {
          sev = 'caution';
        } else if (impactHazards.length > 0) {
          sev = 'advisory';
        } else if (partialDataError) {
          sev = 'advisory';
        }

        setSeverity(sev);
        const reasons = partialDataError && impactHazards.length === 0
          ? ['Hazard data partially unavailable']
          : impactHazards
          .sort((a, b) => impactRank(b.severity) - impactRank(a.severity) || a.distance - b.distance)
          .slice(0, 3)
          .map(hazard => hazard.reason || `${hazard.type}: ${hazard.description || 'review nearby hazard'}`);
        onUpdate && onUpdate(sev, reasons);
        setLoading(false);
      } catch (err) {
        console.error('Hazards fetch failed:', err);
        setSeverity('advisory');
        onUpdate && onUpdate('advisory', ['Hazard data unavailable']);
        setLoading(false);
      }
    };

    fetchHazards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lon, refreshTrigger]);

  const flightImpactHazards = hazardsData.filter(hazard => ['no-go', 'caution', 'advisory'].includes(hazard.severity));
  const primaryMetric = {
    value: flightImpactHazards.length,
    label: flightImpactHazards.length === 1 ? 'nearby hazard found' : 'nearby hazards found'
  };

  const secondaryMetrics = flightImpactHazards.length > 0 ? [
    {
      label: 'Nearest',
      value: `${(flightImpactHazards[0].distance * 1.15078).toFixed(1)} mi`,
      highlight: flightImpactHazards[0].severity === 'caution' || flightImpactHazards[0].severity === 'no-go'
    }
  ] : [];

  const statusMessage = severity === 'go'
    ? 'No nearby hazards found'
    : severity === 'advisory'
    ? (flightImpactHazards.length === 0 ? 'Hazard data partially unavailable' : 'Nearby advisories - ok to fly')
    : severity === 'caution'
    ? 'Very close restriction or marginal hazard'
    : severity === 'no-go'
    ? 'Legal restriction detected'
    : 'Checking hazards...';

  return (
    <DashboardMetricCard
      title="Nearby Hazards"
      icon={WarningIcon}
      status={severity}
      primaryMetric={primaryMetric}
      secondaryMetrics={secondaryMetrics}
      statusMessage={statusMessage}
      loading={loading}
      minHeight={112}
    >
      {hazardsData.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Nearby Items
          </Typography>
          <List dense>
            {hazardsData.slice(0, 5).map((hazard, idx) => (
              <ListItem key={idx} sx={{ px: 0 }}>
                <ListItemText
                  primary={hazard.type || `Hazard #${idx + 1}`}
                  secondary={`${hazard.distance === 0 ? 'At this location' : `${(hazard.distance * 1.15078).toFixed(1)} mi away`}${hazard.description ? ` • ${hazard.description}` : ''}`}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
          {hazardsData.length > 5 && (
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              +{hazardsData.length - 5} more hazards
            </Typography>
          )}
        </Box>
      )}
    </DashboardMetricCard>
  );
};

export default NearbyHazardsCardDashboard;
